import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Copy, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-600 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 h-12";

const empty = { customer_name: "", customer_phone: "", job_description: "", amount: "" };
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const money = (n) => `$${Number(n).toFixed(2)}`;

export default function Owner() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [holds, setHolds] = useState([]);
  const [form, setForm] = useState(empty);
  const [captureAmounts, setCaptureAmounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const headers = () => ({ "X-Owner-Password": sessionStorage.getItem("owner_pw") || "" });

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/owner/holds`, { headers: headers() });
    setHolds(data);
  }, []);

  useEffect(() => {
    document.title = "Owner | Duke Summers Moving";
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    if (sessionStorage.getItem("owner_pw")) {
      load()
        .then(() => setAuthed(true))
        .catch(() => {
          sessionStorage.removeItem("owner_pw");
          setAuthed(false);
        });
    }
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    sessionStorage.setItem("owner_pw", pw);
    try {
      await load();
      setAuthed(true);
    } catch {
      sessionStorage.removeItem("owner_pw");
      toast.error("Wrong password");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const createLink = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Enter the customer name");
    if (!form.job_description.trim()) return toast.error("Enter the job description");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid hold amount");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/owner/holds`,
        { ...form, amount },
        { headers: headers() }
      );
      const url = `${window.location.origin}/pay/${data.token}`;
      setLastLink(url);
      setForm(empty);
      toast.success("Link created");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press to copy");
    }
  };

  const act = async (token, action, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/owner/holds/${token}/${action}`, body || {}, { headers: headers() });
      toast.success("Done");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (token, name) => {
    if (!window.confirm(`Delete this hold link for ${name}? The link will stop working immediately.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/owner/holds/${token}`, { headers: headers() });
      toast.success("Hold deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (!authed) {
    return (
      <div data-testid="owner-login" className="min-h-screen bg-[#1C1C1C] flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm border border-[#3d3d3d] bg-[#262626] p-8">
          <Lock className="h-8 w-8 text-[#FF4500] mb-6" strokeWidth={1.5} />
          <h1 className="font-display font-bold uppercase tracking-tight text-2xl mb-6">Owner Access</h1>
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Password</Label>
          <Input
            data-testid="owner-password"
            type="password"
            className={`${field} mt-2`}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Owner password"
          />
          <button
            type="submit"
            data-testid="owner-login-btn"
            className="mt-5 w-full bg-[#FF4500] text-[#141414] font-bold px-6 py-3 hover:bg-[#E03C00] transition-colors"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="owner-page" className="min-h-screen bg-[#1C1C1C] text-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">
          Payment Holds
        </h1>
        <p className="mt-2 text-neutral-400 text-sm">
          Private page — not linked anywhere. Create a hold link, send it to your
          customer, then capture when the job is complete.
        </p>

        {/* Create link */}
        <form
          onSubmit={createLink}
          data-testid="create-hold-form"
          className="mt-10 border border-[#3d3d3d] bg-[#262626] p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Customer name</Label>
            <Input data-testid="hold-name" className={field} value={form.customer_name} onChange={set("customer_name")} placeholder="Jane Doe" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Customer phone</Label>
            <Input data-testid="hold-phone" className={field} value={form.customer_phone} onChange={set("customer_phone")} placeholder="(555) 123-4567" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Job description</Label>
            <Textarea
              data-testid="hold-description"
              className="bg-[#1C1C1C] border-[#3d3d3d] text-white placeholder:text-neutral-600 focus-visible:ring-[#FF4500] focus-visible:ring-offset-0 min-h-20"
              value={form.job_description}
              onChange={set("job_description")}
              placeholder="e.g. 2 movers, 3 hours — apartment move, Dallas to Plano"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Hold amount (USD)</Label>
            <Input data-testid="hold-amount" type="number" min="1" step="0.01" className={field} value={form.amount} onChange={set("amount")} placeholder="450.00" />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              data-testid="create-link-btn"
              disabled={busy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#141414] font-bold px-8 py-3 hover:bg-[#E03C00] transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Link"}
            </button>
          </div>
          {lastLink && (
            <div data-testid="last-link" className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 border border-[#FF4500]/40 bg-[#1C1C1C] p-4">
              <span className="text-sm text-neutral-200 break-all flex-1">{lastLink}</span>
              <button
                type="button"
                data-testid="copy-link-btn"
                onClick={() => copy(lastLink)}
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white text-sm font-semibold px-4 py-2 hover:bg-white hover:text-[#1C1C1C] transition-colors"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
            </div>
          )}
        </form>

        {/* Holds table */}
        <div className="mt-10 overflow-x-auto border border-[#3d3d3d]">
          <table data-testid="holds-table" className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="bg-[#262626] text-left text-xs uppercase tracking-widest text-neutral-400">
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Authorized</th>
                <th className="px-4 py-3">Day 3 Honor</th>
                <th className="px-4 py-3">Day 29 Expiration</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {holds.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">No holds yet.</td></tr>
              )}
              {holds.map((h) => (
                <tr key={h.token} data-testid={`hold-row-${h.token}`} className="border-t border-[#3d3d3d] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{h.customer_name}</div>
                    <div className="text-neutral-500 text-xs">{h.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold">{money(h.amount)}</td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`hold-status-${h.token}`}
                      className={`inline-block px-2 py-1 text-xs font-bold uppercase tracking-wide ${
                        h.status === "Captured" ? "bg-green-900/50 text-green-300" :
                        h.status === "Authorized" ? "bg-blue-900/50 text-blue-300" :
                        h.status === "Voided" || h.status === "Expired" ? "bg-neutral-800 text-neutral-400" :
                        "bg-[#FF4500]/15 text-[#FF4500]"
                      }`}
                    >
                      {h.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{fmt(h.authorized_at)}</td>
                  <td className="px-4 py-3 text-neutral-300">{fmt(h.honor_deadline)}</td>
                  <td className="px-4 py-3 text-neutral-300">{fmt(h.expires_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2 min-w-[200px]">
                      {h.status === "Link Sent" && (
                        <button
                          type="button"
                          data-testid={`copy-${h.token}`}
                          onClick={() => copy(`${window.location.origin}/pay/${h.token}`)}
                          className="inline-flex items-center justify-center gap-2 border border-white/25 text-white text-xs font-semibold px-3 py-2 hover:bg-white hover:text-[#1C1C1C] transition-colors"
                        >
                          <Copy className="h-3.5 w-3.5" /> Copy Link
                        </button>
                      )}
                      {(h.status === "Link Sent" || h.status === "Expired") && (
                        <button
                          type="button"
                          data-testid={`delete-${h.token}`}
                          disabled={busy}
                          onClick={() => del(h.token, h.customer_name)}
                          className="border border-red-800 text-red-400 text-xs font-semibold px-3 py-2 hover:bg-red-900/40 transition-colors disabled:opacity-60"
                        >
                          Delete
                        </button>
                      )}
                      {h.status === "Authorized" && (
                        <>
                          <div className="flex gap-2">
                            <Input
                              data-testid={`capture-amount-${h.token}`}
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="bg-[#1C1C1C] border-[#3d3d3d] text-white h-9 text-xs w-24"
                              value={captureAmounts[h.token] ?? h.amount}
                              onChange={(e) => setCaptureAmounts((c) => ({ ...c, [h.token]: e.target.value }))}
                            />
                            <button
                              type="button"
                              data-testid={`capture-${h.token}`}
                              disabled={busy}
                              onClick={() => {
                                const amt = parseFloat(captureAmounts[h.token] ?? h.amount);
                                if (window.confirm(`Capture ${money(amt)} from ${h.customer_name}?`)) {
                                  act(h.token, "capture", { amount: amt });
                                }
                              }}
                              className="bg-green-700 text-white text-xs font-bold px-3 py-2 hover:bg-green-600 transition-colors disabled:opacity-60"
                            >
                              Capture
                            </button>
                          </div>
                          <button
                            type="button"
                            data-testid={`reauthorize-${h.token}`}
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Reauthorize the ${money(h.amount)} hold for ${h.customer_name}? This extends the hold.`)) {
                                act(h.token, "reauthorize", {});
                              }
                            }}
                            className="border border-white/25 text-white text-xs font-semibold px-3 py-2 hover:bg-white hover:text-[#1C1C1C] transition-colors disabled:opacity-60"
                          >
                            Reauthorize
                          </button>
                          <button
                            type="button"
                            data-testid={`void-${h.token}`}
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Void the ${money(h.amount)} hold for ${h.customer_name}? This releases the funds and cannot be undone.`)) {
                                act(h.token, "void", {});
                              }
                            }}
                            className="border border-red-800 text-red-400 text-xs font-semibold px-3 py-2 hover:bg-red-900/40 transition-colors disabled:opacity-60"
                          >
                            Void
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
